import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axiosInstance from '../utils/axiosInterceptor';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'candidate';
  profile: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    resume?: string;
    skills?: string[];
    experience?: number;
  };
  createdAt: string;
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateProfile: (profile: Partial<User['profile']>) => Promise<void>;
  isLoading: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  role?: 'candidate';
  profile?: Partial<User['profile']>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem('token') || localStorage.getItem('interview_login_token')
  );
  const [isLoading, setIsLoading] = useState(true);

  // Verify token on app load
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axiosInstance.get('/auth/verify');
          if (response.data.success) {
            setUser(response.data.user);
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    verifyToken();
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      const response = await axiosInstance.post('/auth/login', { email, password });
      
      if (response.data.success) {
        const { token: newToken, user: userData } = response.data;

        localStorage.setItem('token', newToken);
        localStorage.setItem('interview_login_token', newToken);
        localStorage.setItem('interview_login_user', JSON.stringify(userData));
        setToken(newToken);
        setUser(userData);

        toast.success('Login successful!');
      }
    } catch (error: any) {
      // Error is already handled by interceptor, but we can add specific handling here if needed
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await axiosInstance.post('/auth/register', userData);
      
      if (response.data.success) {
        const { token: newToken, user: userInfo } = response.data;
        
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userInfo);
        
        toast.success('Registration successful!');
      }
    } catch (error: any) {
      // Error is already handled by interceptor, but we can add specific handling here if needed
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('interview_login_token');
    localStorage.removeItem('interview_login_user');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (profile: Partial<User['profile']>) => {
    try {
      const response = await axiosInstance.put('/auth/profile', { profile });
      
      if (response.data.success) {
        setUser(response.data.user);
        toast.success('Profile updated successfully!');
      }
    } catch (error: any) {
      // Error is already handled by interceptor, but we can add specific handling here if needed
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    updateProfile,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
